import {
  CircleAlert as CircleAlertIcon,
  RefreshCw as RefreshCwIcon,
  WifiOff as WifiOffIcon,
} from '@lucide/vue'
import { computed, type FunctionalComponent } from 'vue'

import { connectivityService } from '@/services/connectivityService'
import { pluralizeItems } from '@/services/syncCopy'
import { syncService } from '@/services/syncService'
import { useAuthStore } from '@/stores/authStore'
import { useEventStore } from '@/stores/eventStore'

/**
 * Reactive sync-status indicator state, shared by the desktop header (MapHeader) and the
 * mobile floating sync button (WorkspaceMapContainer). Returns the icon to render, its
 * colour/animation classes (size is left to the caller), a human-readable title, plus the
 * ambient-visibility signals from docs/concepts/sync-feedback.md §4.4: a queued-item badge
 * count and `criticalPresence` (states that must stay visible in every UI mode).
 */
export function useSyncStatusIndicator() {
  const eventStore = useEventStore()
  const authStore = useAuthStore()

  const quarantinedCount = computed(() => eventStore.quarantinedEvents.length)
  const badgeCount = computed(() => eventStore.unsyncedEvents.length + quarantinedCount.value)

  const isOfflineLike = computed(
    () => !connectivityService.deviceOnline.value || connectivityService.forceOfflineMode.value,
  )

  // Signed out with work still queued. NOT gated on syncStatus: the unauthenticated sync path
  // leaves the status at 'idle' (never 'error'), so requiring 'error' here would hide the queue
  // entirely — no badge, no button, no path to sign in and flush it (sync-feedback.md §4.4).
  const isSignedOutWithQueue = computed(() => !authStore.isAuthenticated && badgeCount.value > 0)

  // Attention states get a distinct SHAPE, not just a colour (never colour alone — §4.4).
  const iconComponent = computed<FunctionalComponent>(() => {
    if (isOfflineLike.value) {
      return WifiOffIcon
    }
    if (syncService.syncStatus.value === 'error' || quarantinedCount.value > 0) {
      return CircleAlertIcon
    }
    return RefreshCwIcon
  })

  // Colour + animation only — the caller adds size (e.g. `h-[18px] w-[18px]` or `h-3 w-3`).
  // Offline is calm gray, never red: waiting is normal, not an error (§4.4).
  const iconStateClass = computed(() => {
    const status = syncService.syncStatus.value
    if (!connectivityService.deviceOnline.value) return 'text-gray-500'
    if (connectivityService.forceOfflineMode.value) return 'text-amber-500'
    if (status === 'syncing') return 'text-blue-500 animate-spin'
    if (status === 'error' || quarantinedCount.value > 0) return 'text-red-500'
    if (status === 'success') return 'text-green-500'
    return 'text-gray-400'
  })

  const title = computed(() => {
    const count = badgeCount.value
    if (isSignedOutWithQueue.value) {
      return `Signed out — sign in to sync ${pluralizeItems(count)}`
    }
    if (quarantinedCount.value > 0 && syncService.syncStatus.value !== 'syncing') {
      return `${pluralizeItems(quarantinedCount.value)} couldn't be synced`
    }
    if (!connectivityService.deviceOnline.value) {
      return count > 0 ? `Offline — ${pluralizeItems(count)} will sync later` : 'Offline'
    }
    if (connectivityService.forceOfflineMode.value) {
      return count > 0
        ? `Offline mode on — ${pluralizeItems(count)} will sync later`
        : 'Offline mode on'
    }
    if (syncService.syncStatus.value === 'syncing') {
      return count > 0 ? `Syncing… ${pluralizeItems(count)} left` : 'Syncing'
    }
    if (syncService.syncStatus.value === 'error') {
      if (syncService.nextRetryAt.value !== null) {
        return `Sync issue — retrying automatically (attempt ${syncService.retryAttempt.value} of ${syncService.retryMaxAttempts})`
      }
      if (syncService.cooldownUntil.value !== null) {
        return 'Sync issue — will retry soon'
      }
      return 'Sync issue'
    }
    if (syncService.syncStatus.value === 'success') return 'Everything synced'
    return count > 0 ? `${pluralizeItems(count)} waiting to sync` : 'Sync status'
  })

  // "Needs attention" = actively syncing, errored, or offline. When false (idle / just-synced)
  // the indicator can stay calm/unobtrusive; consumers use this to show a background only when
  // it actually matters (calm-technology / progressive disclosure).
  const needsAttention = computed(() => {
    if (isOfflineLike.value || quarantinedCount.value > 0) {
      return true
    }
    const status = syncService.syncStatus.value
    return status === 'syncing' || status === 'error'
  })

  // States that must keep an ambient presence in EVERY UI mode, including track draw/edit and
  // navigation (§4.4 "no blind spots"): an actionable error, a signed-out queue, or an offline
  // backlog. Calm states may still be hidden while those modes own the screen.
  const criticalPresence = computed(() => {
    if (syncService.syncStatus.value === 'error' || quarantinedCount.value > 0) return true
    if (isSignedOutWithQueue.value) return true
    return isOfflineLike.value && badgeCount.value > 0
  })

  // Badge tone follows the state: red only when the user can act (error / rejected items /
  // signed out), amber for offline backlog, gray otherwise.
  const badgeToneClass = computed(() => {
    if (
      syncService.syncStatus.value === 'error' ||
      quarantinedCount.value > 0 ||
      isSignedOutWithQueue.value
    ) {
      return 'bg-red-500 text-white'
    }
    if (isOfflineLike.value) {
      return 'bg-amber-500 text-white'
    }
    return 'bg-gray-400 text-white'
  })

  return {
    iconComponent,
    iconStateClass,
    title,
    needsAttention,
    badgeCount,
    badgeToneClass,
    criticalPresence,
    isSignedOutWithQueue,
  }
}
